import { useEffect, useState } from 'react';
import api from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import { TIPOS_ID } from '../utils/format.js';
import { PageHeader, Modal, Loading, EmptyState } from './UI.jsx';
import { Icon } from './Icons.jsx';
import useContribuyenteLookup from '../hooks/useContribuyenteLookup.js';

// Componente generico para administrar clientes o proveedores.
export default function ContactosCRUD({ tipo }) {
  const toast = useToast();
  const esCliente = tipo === 'clientes';
  const recurso = esCliente ? 'clientes' : 'proveedores';
  const titulo = esCliente ? 'Clientes' : 'Proveedores';
  const subt = esCliente ? 'Tu cartera de clientes' : 'Tus proveedores y distribuidores';

  const [lista, setLista] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  // Fecha de registro. Vacio por defecto: filtrar la cartera a "este mes"
  // haria parecer que los clientes de antes desaparecieron.
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({});
  const [nombreManual, setNombreManual] = useState(false);

  const lookup = useContribuyenteLookup(form.identificacion, { enabled: modal });

  const cargar = () => {
    const params = {};
    if (busqueda) params.q = busqueda;
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    api.get(`/${recurso}`, { params }).then((r) => setLista(r.data.data));
  };
  useEffect(cargar, [busqueda, desde, hasta, recurso]);

  // Autocompleta con Hacienda
  useEffect(() => {
    if (!modal || lookup.estado !== 'ok' || !lookup.datos) return;
    const d = lookup.datos;
    setForm((f) => ({
      ...f,
      tipo_identificacion: d.tipo_identificacion || f.tipo_identificacion || '01',
      identificacion: d.identificacion || f.identificacion,
      nombre: nombreManual && f.nombre?.trim() ? f.nombre : d.nombre || f.nombre,
      ...(esCliente
        ? { codigo_actividad: d.codigo_actividad || f.codigo_actividad || '' }
        : {}),
    }));
  }, [lookup.estado, lookup.datos, modal, nombreManual, esCliente]);

  const abrirNuevo = () => {
    setEditando(null);
    setNombreManual(false);
    lookup.limpiar();
    setForm(
      esCliente
        ? {
            nombre: '',
            tipo_identificacion: '01',
            identificacion: '',
            email: '',
            telefono: '',
            direccion: '',
            codigo_actividad: '',
          }
        : { nombre: '', identificacion: '', email: '', telefono: '', direccion: '' }
    );
    setModal(true);
  };
  const abrirEditar = (item) => {
    setEditando(item.id);
    setNombreManual(true);
    lookup.limpiar();
    setForm({ ...item });
    setModal(true);
  };

  const guardar = async () => {
    if (!form.nombre) {
      toast.error('El nombre es requerido');
      return;
    }
    try {
      const payload = {
        ...form,
        identificacion: String(form.identificacion || '').replace(/\D/g, '') || form.identificacion || '',
      };
      if (editando) await api.put(`/${recurso}/${editando}`, payload);
      else await api.post(`/${recurso}`, payload);
      toast.success('Guardado');
      setModal(false);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const eliminar = async (item) => {
    if (!confirm(`Eliminar ${item.nombre}?`)) return;
    await api.delete(`/${recurso}/${item.id}`);
    toast.success('Eliminado');
    cargar();
  };

  return (
    <div>
      <PageHeader title={titulo} subtitle={subt}>
        <button className="btn btn-primary" onClick={abrirNuevo}>
          <Icon.plus /> Nuevo
        </button>
      </PageHeader>

      <div
        className="card"
        style={{
          padding: 14,
          marginBottom: 16,
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        <div className="field" style={{ margin: 0, flex: '1 1 220px', position: 'relative' }}>
          <label>Buscar</label>
          <span
            style={{
              position: 'absolute',
              left: 12,
              bottom: 11,
              color: 'var(--text-muted)',
            }}
          >
            <Icon.search width={16} height={16} />
          </span>
          <input
            className="input"
            style={{ paddingLeft: 36 }}
            placeholder="Nombre, cedula, correo…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Registrado desde</label>
          <input className="input" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Hasta</label>
          <input className="input" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        {(busqueda || desde || hasta) && (
          <button className="btn btn-outline btn-sm"
            onClick={() => { setBusqueda(''); setDesde(''); setHasta(''); }}>
            Limpiar
          </button>
        )}
        {lista && (
          <span className="muted" style={{ fontSize: 13 }}>
            {lista.length} resultado(s)
          </span>
        )}
      </div>

      {!lista ? (
        <Loading />
      ) : lista.length === 0 ? (
        <EmptyState
          icon={esCliente ? 'clientes' : 'proveedores'}
          titulo={`Sin ${titulo.toLowerCase()}`}
          texto="Agrega el primero con el boton de arriba."
        />
      ) : (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Identificacion</th>
                <th>Ubicacion</th>
                <th>Telefono</th>
                <th>Correo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lista.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600 }}>{c.nombre}</td>
                  <td className="muted">{c.identificacion || '—'}</td>
                  <td className="muted" style={{ maxWidth: 220 }} title={c.direccion || ''}>
                    {c.direccion || '—'}
                  </td>
                  <td className="muted">{c.telefono || '—'}</td>
                  <td className="muted">{c.email || '—'}</td>
                  <td>
                    <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => abrirEditar(c)}>
                        <Icon.edit width={15} height={15} />
                      </button>
                      <button
                        className="btn btn-sm"
                        style={{ color: 'var(--danger)' }}
                        onClick={() => eliminar(c)}
                      >
                        <Icon.trash width={15} height={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={
          editando
            ? `Editar ${titulo.slice(0, -1).toLowerCase()}`
            : `Nuevo ${titulo.slice(0, -1).toLowerCase()}`
        }
      >
        <p className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
          Digite la cedula fisica (9), juridica (10) o DIMEX (11–12). Los datos se cargan desde
          Hacienda.
        </p>

        {esCliente && (
          <div className="grid-2" style={{ gap: 12 }}>
            <div className="field">
              <label>Tipo de identificacion</label>
              <select
                className="select"
                value={form.tipo_identificacion || '01'}
                onChange={(e) => setForm({ ...form, tipo_identificacion: e.target.value })}
              >
                {TIPOS_ID.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Numero de identificacion</label>
              <input
                className="input mono"
                value={form.identificacion || ''}
                onChange={(e) => {
                  setNombreManual(false);
                  setForm({ ...form, identificacion: e.target.value });
                }}
                placeholder="9 a 12 digitos"
              />
            </div>
          </div>
        )}
        {!esCliente && (
          <div className="field">
            <label>Cedula / identificacion</label>
            <input
              className="input mono"
              value={form.identificacion || ''}
              onChange={(e) => {
                setNombreManual(false);
                setForm({ ...form, identificacion: e.target.value });
              }}
              placeholder="9 a 12 digitos"
            />
          </div>
        )}

        <div style={{ minHeight: 22, marginBottom: 6, fontSize: 12 }}>
          {lookup.cargando && <span className="muted">Consultando Hacienda…</span>}
          {lookup.estado === 'ok' && lookup.datos && (
            <span style={{ color: 'var(--success, #16a34a)', fontWeight: 600 }}>
              Hacienda: {lookup.datos.situacion?.estado || 'OK'}
              {lookup.datos.regimen ? ` · ${lookup.datos.regimen}` : ''}
            </span>
          )}
          {lookup.estado === 'not_found' && (
            <span style={{ color: 'var(--warning, #d97706)' }}>
              No aparece en Hacienda. Complete a mano.
            </span>
          )}
          {lookup.estado === 'error' && (
            <span style={{ color: 'var(--danger)' }}>{lookup.error}</span>
          )}
        </div>

        <div className="field">
          <label>Nombre *</label>
          <input
            className="input"
            value={form.nombre || ''}
            onChange={(e) => {
              setNombreManual(true);
              setForm({ ...form, nombre: e.target.value });
            }}
            placeholder="Se completa solo con la consulta"
          />
        </div>

        <div className="grid-2" style={{ gap: 12 }}>
          <div className="field">
            <label>Telefono</label>
            <input
              className="input"
              value={form.telefono || ''}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Correo</label>
            <input
              className="input"
              type="email"
              value={form.email || ''}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
        </div>
        <div className="field">
          <label>Ubicacion / direccion</label>
          <input
            className="input"
            value={form.direccion || ''}
            onChange={(e) => setForm({ ...form, direccion: e.target.value })}
            placeholder={
              esCliente
                ? 'Provincia, canton, distrito, senas exactas'
                : 'Direccion del proveedor'
            }
          />
        </div>
        {esCliente && (
          <div className="field">
            <label>Codigo de actividad economica</label>
            <input
              className="input mono"
              value={form.codigo_actividad || ''}
              onChange={(e) => setForm({ ...form, codigo_actividad: e.target.value })}
              placeholder="Se carga desde Hacienda si existe"
            />
            {lookup.datos?.actividad_descripcion && (
              <span className="muted" style={{ fontSize: 12 }}>
                {lookup.datos.actividad_descripcion}
              </span>
            )}
          </div>
        )}
        <div className="flex gap-sm" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn btn-outline" onClick={() => setModal(false)}>
            Cancelar
          </button>
          <button className="btn btn-primary" onClick={guardar}>
            Guardar
          </button>
        </div>
      </Modal>
    </div>
  );
}
